import { useState, useEffect } from "react";
import Button from "../base/Button";
import { Link, useLocation } from "react-router-dom";

import Project1Image from "../../assets/logo/logo-light.webp";

const serviceItems = [
  {
    title: "Drafting Services",
    description: "Precision CAD & BIM solutions including architectural floor plans and 3D renderings.",
    icon: "ri-draft-line",
    link: "/services/drafting",
    color: "#e18033"
  },
  {
    title: "Takeoff Services",
    description: "Accurate material quantity takeoffs and cost estimation for confident bidding.",
    icon: "ri-calculator-line",
    link: "/services/takeoff",
    color: "#0d9488"
  }
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  // Check for saved dark mode preference or default to false
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;

    if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  localStorage.setItem("theme", "light");

  return (
    <header className="bg-gray-900 shadow-sm border-b border-gray-700 sticky top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img
                src={isDarkMode ? Project1Image : Project1Image}
                alt="Designs By DMI"
                className="h-16 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname === "/" ? "text-[#e18033]" : ""
                }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative group py-8"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <div
                className={`flex items-center gap-1 text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname.startsWith("/services") ? "text-[#e18033]" : ""
                  }`}
              >
                Services
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
              </div>

              {/* Dropdown Menu */}
              <div
                className={`absolute top-full -left-10 w-[450px] bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-4 transition-all duration-300 origin-top
                ${isServicesOpen ? 'opacity-100 translate-y-0 visible scale-100' : 'opacity-0 -translate-y-4 invisible scale-95'}`}
              >
                <div className="grid gap-2">
                  {serviceItems.map((service) => (
                    <Link
                      key={service.title}
                      to={service.link}
                      className="group/item flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-all duration-300"
                      onClick={() => setIsServicesOpen(false)}
                    >
                      <div
                        className="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center transition-all duration-300 group-hover/item:scale-110"
                        style={{ backgroundColor: `${service.color}22`, color: service.color }}
                      >
                        <i className={`${service.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h4 className="text-white font-bold mb-1 group-hover/item:text-[#e18033] transition-colors">
                          {service.title}
                        </h4>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/process"
              className={`text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname === "/process" ? "text-[#e18033]" : ""
                }`}
            >
              Process
            </Link>
            <Link
              to="/portfolio"
              className={`text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname === "/portfolio" ? "text-[#e18033]" : ""
                }`}
            >
              Portfolio
            </Link>
            <Link
              to="/about"
              className={`text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname === "/about" ? "text-[#e18033]" : ""
                }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={`text-white hover:text-[#e18033] dark:hover:text-[#e18033] transition-colors font-medium cursor-pointer ${location.pathname === "/contact" ? "text-[#e18033]" : ""
                }`}
            >
              Contact
            </Link>
          </div>

          {/* Dark Mode Toggle & CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://partner.designsbydmi.com/"
              target="__Blank__"
            >
              <Button variant="outline" className="w-full">
                Sign In
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-[#e18033] dark:hover:text-[#e18033] cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-menu-line text-xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Off-canvas */}
        <div 
          className={`fixed inset-0 z-50 lg:hidden transition-all duration-500 ease-in-out ${isMenuOpen ? 'visible' : 'invisible'}`}
        >
          {/* Backdrop */}
          <div 
            className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Off-canvas Menu Content */}
          <div 
            className={`absolute top-0 left-0 w-[300px] h-full bg-gray-900 shadow-2xl transition-transform duration-500 ease-out flex flex-col ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <img
                src={Project1Image}
                alt="Designs By DMI"
                className="h-10 w-auto"
              />
              <button
                onClick={() => setIsMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 text-white hover:text-[#e18033] transition-colors"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Menu Links */}
            <div className="flex-1 overflow-y-auto py-6 px-6 space-y-2">
              <Link
                to="/"
                className={`block px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === "/" ? "bg-[#e18033]/10 text-[#e18033]" : "text-white hover:bg-white/5"}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              
              {/* Mobile Services Sub-menu */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className={`w-full flex justify-between items-center px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname.startsWith("/services") ? "text-[#e18033]" : "text-white hover:bg-white/5"}`}
                >
                  Services
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isMobileServicesOpen ? 'max-h-60 mt-2 pb-2' : 'max-h-0'}`}>
                  <div className="pl-4 space-y-1">
                    {serviceItems.map((service) => (
                      <Link
                        key={service.title}
                        to={service.link}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-[#e18033] hover:bg-white/5 transition-all duration-300 text-sm"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsMobileServicesOpen(false);
                        }}
                      >
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${service.color}22`, color: service.color }}
                        >
                          <i className={`${service.icon} text-lg`}></i>
                        </div>
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/process"
                className={`block px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === "/process" ? "bg-[#e18033]/10 text-[#e18033]" : "text-white hover:bg-white/5"}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Process
              </Link>
              <Link
                to="/portfolio"
                className={`block px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === "/portfolio" ? "bg-[#e18033]/10 text-[#e18033]" : "text-white hover:bg-white/5"}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Portfolio
              </Link>
              <Link
                to="/about"
                className={`block px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === "/about" ? "bg-[#e18033]/10 text-[#e18033]" : "text-white hover:bg-white/5"}`}
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link
                to="/contact"
                className={`block px-4 py-3 rounded-xl transition-all duration-300 font-medium ${location.pathname === "/contact" ? "bg-[#e18033]/10 text-[#e18033]" : "text-white hover:bg-white/5"}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
            </div>

            {/* Menu Footer */}
            <div className="p-6 border-t border-gray-800">
              <a
                href="https://partner.designsbydmi.com/"
                target="__Blank__"
                className="block w-full"
              >
                <Button variant="outline" className="w-full justify-center">
                  Sign In
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
